import { useContext, useMemo } from "react"
import { useAppSelector } from "@/reducers/hooks"
import { selectAccountChatByJid } from "@/reducers/accountChatsSlice"
import { selectForeignChatActivity } from "@/reducers/foreignChatActivitySlice"
import { messageAuthorName } from "@/reducers/profilesSliceSelectors"
import useRouteBadges from "@/components/chatCard/useRouteBadges"
import { useForeignChatAvatar } from "@/hooks/useForeignChatAvatar"
import { relativeTimeLabel } from "@/components/chatCard/utils"
import {
  formatLastEventText,
  resolveLastEventAuthorName,
} from "@/lib/lastEventText"
import MessageHelpers from "@/lib/messageHelpers"
import { XmppContext } from "@/stream/xmppClient"
import { useTranslation } from "react-i18next"

/**
 * The same card data as `useChatCardData`, for a chat belonging to another
 * dispatcher.
 *
 * Everything about the truck comes from the account-wide pubsub slices, which
 * the client already holds for every vehicle — so the badges need no request of
 * their own. Only the preview line has to be fetched, and
 * `useForeignChatActivity` already does that for an expanding section.
 *
 * Unread counts are deliberately absent: what the owner has read lives in their
 * client, not on the server, so there is nothing to show.
 */
export const useForeignChatCardData = (jid: string, chatName: string) => {
  const { t } = useTranslation()
  const { myJid } = useContext(XmppContext)
  const { driverParticipant, ...routeBadges } = useRouteBadges(jid)
  const { photo, name: driverName, color } = useForeignChatAvatar(jid, chatName)

  const accountChatSelector = useMemo(() => selectAccountChatByJid(jid), [jid])
  const accountChat = useAppSelector(accountChatSelector)

  const activity = useAppSelector(selectForeignChatActivity)[jid]
  const lastEvent = activity?.lastEvent

  const authorName = useAppSelector(messageAuthorName(lastEvent, myJid))

  const lastEventText =
    activity?.status === "denied"
      ? // Shown instead of a preview for a room our office account was never
        // let into.
        t("app.chatList.noArchiveAccess")
      : formatLastEventText({ event: lastEvent, myJid, accountChat })

  const isLastEventToDriver = Boolean(
    lastEvent &&
      driverParticipant &&
      !MessageHelpers.hasComeFromJid(lastEvent, driverParticipant.jid),
  )

  return {
    // `ChatCard` derives initials from this, so it takes the driver's name —
    // and, when the chat carries none, the "?" the row showed before.
    avatarName: driverName ?? "?",
    avatarPhoto: photo,
    avatarColor: color ?? "var(--defaultAvatarColor)",

    lastEventText,
    lastEventAuthorName:
      activity?.status === "denied"
        ? ""
        : resolveLastEventAuthorName({ authorName, event: lastEvent }),
    lastEventRelativeTime: relativeTimeLabel(activity?.lastMessageAt),
    isLastEventToDriver,

    ...routeBadges,
  }
}

export default useForeignChatCardData
